/**
 * LevelsSheet — "Your Fluency Journey" levels list.
 *
 * iOS source: ParrotLevelsSheet.tsx + LevelRow.tsx. Bottom sheet on iOS listing
 * all 50 levels; on the PWA it renders as a centered modal (max-width 480) on
 * desktop and a near-full-width sheet on mobile. Auto-scrolls to center the
 * current level on open. Footer "Got it" button.
 *
 * Rendered through a portal to <body> so the full-screen scrim paints above the
 * fixed bottom NavBar (iOS sheet is full-screen and covers the tab bar). The
 * sheet has no top-right close button — a blue "Got it" button pinned at the
 * bottom dismisses it, matching iOS (ContinueButton in footerArea).
 *
 * Header copy: "Your Fluency Journey" / "Keep going to level up!"
 *
 * Uses SHEET_LEVELS (per-row goal text) + the user's current level + progress
 * to color completed / current / future rows.
 */
import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { colors, tokens } from "@/ui";
import { SHEET_LEVELS } from "@/data/parrotLevels";
import { getLevelIconUrl } from "@/components/Profile/profileAssets";
import { computeLevelProgress } from "./levelProgress";
import type { ProgressStats } from "./levelProgress";
import LevelRow from "./LevelRow";
import type { LevelBadgeState } from "./LevelBadge";

export interface LevelsSheetProps {
  open: boolean;
  /** Current level id (1..50). */
  currentLevel: number;
  /**
   * Full progress stats; each current row's bar is computed against its own
   * checkType (iOS parity), not a single listening-hours fraction.
   */
  progressStats: ProgressStats;
  onClose: () => void;
}

const LevelsSheet: FC<LevelsSheetProps> = ({
  open,
  currentLevel,
  progressStats,
  onClose,
}) => {
  const currentRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && currentRowRef.current) {
      // Defer so the modal is laid out before scrolling.
      const t = setTimeout(() => {
        currentRowRef.current?.scrollIntoView({
          block: "center",
          behavior: "auto",
        });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: tokens.zIndex.sheet,
        background: "var(--overlay-scrim-medium)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          maxHeight: "80dvh",
          background: colors.white,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: tokens.shadows.modal,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Drag handle (iOS ParrotLevelsSheet styles.handle). */}
        <div
          style={{
            paddingTop: 10,
            paddingBottom: 6,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: colors.greyLight,
            }}
          />
        </div>

        {/* Header */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "4px 24px 12px",
          }}
        >
          <span
            style={{
              fontSize: "var(--base-font-size-xlarge)",
              fontWeight: 700,
              color: colors.primary,
            }}
          >
            Your Fluency Journey
          </span>
          <span
            style={{
              fontSize: "var(--base-font-size-small)",
              color: colors.muted,
            }}
          >
            Keep going to level up!
          </span>
        </div>

        {/* Scrollable level list */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 24px 18px",
          }}
        >
          {SHEET_LEVELS.map((lvl, i) => {
            let state: LevelBadgeState;
            if (lvl.level < currentLevel) state = "completed";
            else if (lvl.level === currentLevel) state = "current";
            else state = "future";

            const connectorColor =
              lvl.level <= currentLevel
                ? lvl.level === currentLevel
                  ? colors.blue
                  : colors.green
                : colors.greyLight;

            const row = (
              <LevelRow
                level={lvl.level}
                name={lvl.name}
                emoji={lvl.emoji}
                iconUrl={getLevelIconUrl(lvl.level)}
                requirement={lvl.requirement}
                state={state}
                progress={computeLevelProgress(lvl, currentLevel, progressStats)}
                showConnector={i > 0}
                connectorColor={connectorColor}
                isLast={i === SHEET_LEVELS.length - 1}
              />
            );

            return (
              <div
                key={lvl.level}
                ref={state === "current" ? currentRowRef : undefined}
              >
                {row}
              </div>
            );
          })}
        </div>

        {/* Footer — pinned "Got it" button (iOS footerArea + ContinueButton). */}
        <div
          style={{
            padding: "16px 24px calc(16px + env(safe-area-inset-bottom))",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              height: 52,
              borderRadius: 16,
              background: colors.blue,
              color: colors.white,
              fontSize: "var(--base-font-size-large)",
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LevelsSheet;
